import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gifService } from 'features/gif-maker/services/gifMakerApi';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import CardSkeleton from '../components/Skeletons/CardSkeleton';
import GifCard from '../components/Gif-Maker/GifCard';
import { PlusIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LoadingSkeleton from '../components/Skeletons/LoadingSkeleton';

export const GifMaker = () => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const queryClient = useQueryClient();

  const { data: userGifs, isLoading } = useQuery({
    queryKey: ['user-gifs'],
    queryFn: async () => {
      const response = await gifService.getUserGifs();
      return response.gifs;
    },
    enabled: isAuthenticated,
  });

  const deleteGif = useMutation({
    mutationKey: ['gif-delete'],
    mutationFn: gifService.deleteGifById,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-gifs'] });
      toast.success('Gif deleted successfully');
    },
  });

  const updateGif = useMutation({
    mutationKey: ['edit-gif'],
    mutationFn: gifService.updateGifById,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-gifs'] });
      toast.success('Gif edited successfully');
    },
  });

  const handleDelete = (id: number) => {
    deleteGif.mutate(id);
  };

  const handleUpdate = async (
    e: React.FormEvent,
    id: number,
    fileName: string
  ) => {
    e.preventDefault();
    try {
      await updateGif.mutateAsync({ id, fileName });
    } catch (error) {
      console.error(error);
      toast.error('Failed to update gif');
    }
  };
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="mx-auto rounded-lg rounded-t-none p-4 text-text-primary lg:p-16 w-full max-w-[1400px]">
      <div className="flex flex-col lg:flex-row w-full justify-between items-start lg:items-center gap-4 lg:gap-0">
        <div className="w-full lg:w-auto">
          <h2 className="text-[20px] lg:text-[24px] font-bold">Your GIFs</h2>
          <p className="text-[12px] lg:text-[14px]  mt-1">
            All your GIFs are safely stored here in one place. Easily view,
            organize, and manage them here.
          </p>
        </div>
        <div className="w-full lg:w-auto">
          <div className="hidden sm:flex gap-1 cursor-pointer text-primary items-center justify-start lg:justify-end">
            <PlusIcon size={20} />
            <div
              className="underline text-[14px] lg:text-[16px]"
              onClick={() => navigate('/gif-maker/get-started')}
            >
              Generate New GIFs
            </div>
          </div>
        </div>
      </div>
      {userGifs?.length === 0 ? (
        <div className="w-full">
          <LoadingSkeleton text="GIFs" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 my-7">
          {isLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            userGifs?.length &&
            userGifs.map((gif: any) => (
              <div key={gif.id}>
                <GifCard
                  id={gif.id}
                  title={gif.title}
                  handleDelete={() => handleDelete(gif.id)}
                  handleUpdate={handleUpdate}
                  url={gif.url}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
